import {
  Grid,
  Typography,
  Checkbox,
  FormControlLabel,
  makeStyles,
  Theme,
} from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { useFormikContext } from 'formik'
import {
  SemiConfigFormValues,
  getInitialValuesBasedOnProduct,
  useProductFromQueryParam,
} from './ProductsConfiguration'
import useSearch from './useSearch'
import HoverToolTip from '@src/components/HoverToolTip'
import { SemiConfigProductTypes } from './enum'
import CommonCheckbox from '@src/components/CommonCheckbox'
import StyledRadio from '@src/routes/Configurators/utils/ConfiguratorCustomRadioButton'
import ValidationErrors from '@src/routes/Configurators/validations/ValidationErrors'
import useSpecifications from './useSpecifications'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    paddingTop: theme.spacing(8),
  },
  section: {
    paddingTop: theme.spacing(6),
  },
  speciesContent: {
    paddingTop: theme.spacing(4),
  },
  checkboxGroup: {
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(4),
  },
  commoncheckboxGroup: {
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(4),
  },
  radiobuttons: {
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(1),
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(4),
  },
  checkbox: {
    '& .MuiCheckbox-root': {
      paddingLeft: 0,
    },
  },
}))

const TrcVersionAndValidation = () => {
  const classes = useStyles()
  const intl = useIntl()
  const formik = useFormikContext<SemiConfigFormValues>()
  const { searchQuery } = useSearch()
  const product = useProductFromQueryParam()
  const initialValues = getInitialValuesBasedOnProduct(product)
  const { isORFVectorSelected } = useSpecifications()

  const handleChange = async (
    value: string,
    checked: boolean,
    facetName: string
  ) => {
    let updatedFacet = [...formik.values.searchData.facet]
    const selectedfacet = `${facetName}:${value}`
    if (facetName === 'vector') {
      updatedFacet = updatedFacet.filter(
        (facet) => !facet.startsWith('vector:')
      )
    }

    if (checked) {
      if (!updatedFacet.includes(selectedfacet)) {
        // If this is a matchType facet → remove "matchType:NONE" if any
        if (facetName === 'matchType') {
          updatedFacet = updatedFacet.filter(
            (facet) => facet !== 'matchType:NONE'
          )
        }
        updatedFacet.push(selectedfacet)
      }
    } else {
      updatedFacet = updatedFacet.filter((facet) => facet !== selectedfacet)
      // If all matchTypes are now unchecked → add "matchType:NONE"
      if (facetName === 'matchType') {
        const hasAnyMatchType = updatedFacet.some((facet) =>
          facet.startsWith('matchType:')
        )
        if (!hasAnyMatchType) {
          updatedFacet.push('matchType:NONE')
        }
      }
    }

    formik.setFieldValue('searchData.facet', updatedFacet, false)
    formik.setFieldValue('selectedClones', [], false)
    formik.setFieldValue('specifications', initialValues.specifications, false)
    formik.setFieldValue('searchData.page', 1, false)
    formik.setFieldValue('searchData.count', '10', false)
    formik.setFieldValue(
      'searchData.sort',
      initialValues.searchData.sort,
      false
    )
    formik.setFieldValue(
      'searchData.order',
      initialValues.searchData.order,
      false
    )

    await searchQuery({ facet: updatedFacet })
  }

  const isOptionSelected = (value: string, name: string): boolean => {
    return formik.values.searchData.facet.includes(`${name}:${value}`)
  }

  return (
    <Grid container className={classes.container}>
      {product !== SemiConfigProductTypes.ORF ? (
        <>
          <Grid item xs={12} className={classes.section}>
            <Typography variant="h3">
              <FormattedMessage
                id="SEMI_CONF_TRC_VERSION_shrna"
                defaultMessage="TRC Version"
              />{' '}
              <HoverToolTip
                fontSize="19.25"
                toolTipText={
                  <FormattedMessage
                    id="CONF_TRC1_AND_TRC2"
                    defaultMessage="TRC1 and TRC2 vectors are nearly identical, except TRC2 which includes the WPRE feature. Some sequences are duplicated in both vectors, differing only by clone ID numbers."
                  />
                }
              />
            </Typography>

            <div className={classes.checkboxGroup}>
              <FormControlLabel
                className={classes.checkbox}
                control={
                  <Checkbox
                    checked={isOptionSelected('1', 'trc')}
                    onChange={(e) => handleChange('1', e.target.checked, 'trc')}
                    color="primary"
                  />
                }
                label={
                  <FormattedMessage id="SEMI_CONF_TRC1" defaultMessage="TRC1" />
                }
              />
              <FormControlLabel
                className={classes.checkbox}
                control={
                  <Checkbox
                    checked={isOptionSelected('2', 'trc')}
                    onChange={(e) => handleChange('2', e.target.checked, 'trc')}
                    color="primary"
                  />
                }
                label={
                  <FormattedMessage id="SEMI_CONF_TRC2" defaultMessage="TRC2" />
                }
              />
            </div>
          </Grid>
          <Grid item xs={12} className={classes.section}>
            <Typography variant="h3">
              <FormattedMessage
                id="SEMI_CONF_SHRNA_TRC_VALIDATION_LABEL_shrna"
                defaultMessage="TRC Validation"
              />
            </Typography>
            <div className={classes.checkboxGroup}>
              <FormControlLabel
                className={classes.checkbox}
                control={
                  <Checkbox
                    checked={isOptionSelected('Yes', 'validated')}
                    onChange={(e) =>
                      handleChange('Yes', e.target.checked, 'validated')
                    }
                    color="primary"
                  />
                }
                label={<FormattedMessage id="YES" defaultMessage="Yes" />}
              />
              <FormControlLabel
                className={classes.checkbox}
                control={
                  <Checkbox
                    checked={isOptionSelected('No', 'validated')}
                    onChange={(e) =>
                      handleChange('No', e.target.checked, 'validated')
                    }
                    color="primary"
                  />
                }
                label={<FormattedMessage id="NO" defaultMessage="No" />}
              />
            </div>
          </Grid>
        </>
      ) : (
        <>
          <Grid item xs={12}>
            <Typography variant="h3">
              <FormattedMessage
                id="SEMI_CONF_TRC_VERSION_shrna"
                defaultMessage="TRC Version"
              />
            </Typography>
            <Grid className={classes.speciesContent}>
              <FormattedMessage id="SEMI_CONF_TRC3" defaultMessage="TRC3" />
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.section}>
            <Typography variant="h3">
              <FormattedMessage id="ORF_VECTOR" defaultMessage="Vector" />
              <HoverToolTip
                fontSize="19.25"
                toolTipText={
                  <FormattedMessage
                    id="CONF_ORF_VECTOR"
                    defaultMessage={
                      'The pDONR223 is a Gateway Donor vector.  Each ORF donor contains spectinomycin and can be used with Gateway Destination vectors containing Kanamycin, Ampicillin etc. {br} The pLX_TRC317 are ready to use lentiviral ORF vector with puromycin selection.  Each ORF clone contains a unique 24 base pair barcode for easy confirmation.'
                    }
                    values={{ br: <br /> }}
                  />
                }
              />
            </Typography>
            <div className={classes.radiobuttons}>
              <FormControlLabel
                control={
                  <StyledRadio
                    checked={isOptionSelected('pDONR223', 'vector')}
                    onChange={(e) =>
                      handleChange('pDONR223', e.target.checked, 'vector')
                    }
                    color="primary"
                  />
                }
                label={
                  <FormattedMessage
                    id="ORF_VECTOR_DONR"
                    defaultMessage="pDONR223"
                  />
                }
              />
              <FormControlLabel
                control={
                  <StyledRadio
                    checked={isOptionSelected('pLX_TRC317', 'vector')}
                    onChange={(e) =>
                      handleChange('pLX_TRC317', e.target.checked, 'vector')
                    }
                    color="primary"
                  />
                }
                label={
                  <FormattedMessage
                    id="ORF_VECTOR_TRC3"
                    defaultMessage="pLX_TRC317"
                  />
                }
              />
            </div>
            <div>
              {isORFVectorSelected &&
                formik.values.displayPageError &&
                formik.values.completedStep >= 1 && (
                  <ValidationErrors
                    errors={[
                      intl.formatMessage({
                        id: 'CONF_REQUIRED_FIELD',
                        defaultMessage: 'Required Field',
                      }),
                    ]}
                    configuratorsRedesign
                  />
                )}
            </div>
          </Grid>
          <Grid item xs={12} className={classes.section}>
            <Typography variant="h3">
              <FormattedMessage
                id="ORF_MATCH_TYPE"
                defaultMessage="Match Type"
              />
              <HoverToolTip
                fontSize="19.25"
                toolTipText={
                  <FormattedMessage
                    id="CONF_ORF_Match_type"
                    defaultMessage={
                      'ORF Clones are classified on stringency to the corresponding NCBI Refseq transcript.{br}Perfect Match: the ORF matches 100% in both nucleotide and protein sequence.{br}Near Perfect Match: the ORF has at least a 99% protein match (if coding) or at least a 99% nucleotide match (if non-coding).{br}Lower Match: the ORF has a good ‘partial’ match but may contain large contiguous indels indicating e.g. truncation or a missing/extra exon. Score is calculated using protein match (if coding) or nucleotide match (if non-coding).'
                    }
                    values={{ br: <br /> }}
                  />
                }
              />
            </Typography>
            <div className={classes.commoncheckboxGroup}>
              <FormControlLabel
                className={classes.checkbox}
                control={
                  <CommonCheckbox
                    checked={isOptionSelected('Perfect', 'matchType')}
                    onChange={(e) =>
                      handleChange('Perfect', e.target.checked, 'matchType')
                    }
                    color="primary"
                  />
                }
                label={
                  <FormattedMessage id="PERFECT" defaultMessage="Perfect" />
                }
              />
              <FormControlLabel
                className={classes.checkbox}
                control={
                  <CommonCheckbox
                    checked={isOptionSelected('NearPerfect', 'matchType')}
                    onChange={(e) =>
                      handleChange('NearPerfect', e.target.checked, 'matchType')
                    }
                    color="primary"
                  />
                }
                label={
                  <FormattedMessage
                    id="NEAR_PERFECT"
                    defaultMessage="NearPerfect"
                  />
                }
              />
              <FormControlLabel
                className={classes.checkbox}
                control={
                  <CommonCheckbox
                    checked={isOptionSelected('Lower', 'matchType')}
                    onChange={(e) =>
                      handleChange('Lower', e.target.checked, 'matchType')
                    }
                    color="primary"
                  />
                }
                label={<FormattedMessage id="LOWER" defaultMessage="Lower" />}
              />
            </div>
          </Grid>
        </>
      )}
    </Grid>
  )
}

export default TrcVersionAndValidation
